/**
 * Logo ground detection for the welcome-pack card.
 *
 * Real scraped logos aren't all transparent stencils. Some carry a baked
 * background (Drain Doctor is white artwork on a hard black rectangle;
 * Champion and Canyon Valley sit on white), and Cloudinary's background
 * removal add-on isn't enabled on this account, so the card has to cope with
 * the asset as it is: print such a logo on its own ground as a deliberate
 * tile instead of letting a black slab or a white seam land on the stock.
 *
 * Extracted from /lab/pack so the invite page can print the same card.
 */

import { useEffect, useState } from 'react';

export type Ground = 'none' | 'dark' | 'light';

const groundCache = new Map<string, Ground>();

/**
 * Ground implied directly by the generation-time `theme.logoDarkBackground`
 * flag (fnd-lw0/fnd-p84) — no canvas, no image fetch. `undefined` means the
 * flag is absent (a bundle generated before that work landed, or an identity
 * producer that hasn't been updated to forward it yet); the caller should
 * fall back to `measureGround`/`useLogoGround`'s live probe in that case.
 *
 * Mapping:
 *  - `true` → `'dark'`. This is exactly the signal `measureGround`'s edge
 *    branch flags as `'dark'` (a mostly-opaque, mostly-dark border — a baked
 *    dark background or a black-slab logo): both are sampled the same way,
 *    edge pixels averaged for brightness (see apps/clonerator/lib/remove-bg.ts
 *    `analyzeEdges`), just once at generation time instead of on every paint.
 *    This is the readability-critical case (a dark logo needs its dark tile
 *    or it reads as a stray rectangle on the card stock), so it maps 1:1.
 *  - `false` → `'none'`. The flag alone can't distinguish "plain transparent
 *    logo, no tile needed" (the common case) from "opaque logo with a baked
 *    *light* background" (`measureGround`'s `'light'` verdict — a cosmetic
 *    ring so a white tile doesn't float borderless on white card stock),
 *    because the wire payload doesn't carry `logoTransparent` yet. `'none'`
 *    is the safe default: skipping the rare `'light'` ring is a cosmetic-only
 *    miss, not a legibility one.
 */
export function groundFromFlag(logoDarkBackground: boolean | undefined): Ground | null {
  if (logoDarkBackground === undefined) return null;
  return logoDarkBackground ? 'dark' : 'none';
}

/** Reads the border ring + overall ink of a logo to decide the tile it needs. */
export async function measureGround(url: string): Promise<Ground> {
  const cached = groundCache.get(url);
  if (cached) return cached;
  const img = new Image();
  img.crossOrigin = 'anonymous';
  const loaded = await new Promise<boolean>(resolve => {
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
  let verdict: Ground = 'none';
  if (loaded) {
    const N = 64;
    const canvas = document.createElement('canvas');
    canvas.width = N;
    canvas.height = N;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(img, 0, 0, N, N);
      try {
        const { data } = ctx.getImageData(0, 0, N, N);
        const lum = (i: number) =>
          (0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2]) / 255;
        const alpha = (x: number, y: number) =>
          x < 0 || y < 0 || x >= N || y >= N ? 0 : data[(y * N + x) * 4 + 3];
        let edge = 0;
        let edgeLum = 0;
        let ink = 0;
        let inkLum = 0;
        // Near-white ink with transparency around it — invisible on paper
        // unless the whole logo gets a dark ground under it.
        let floatingWhite = 0;
        for (let y = 0; y < N; y++) {
          for (let x = 0; x < N; x++) {
            const i = (y * N + x) * 4;
            if (data[i + 3] < 32) continue;
            const l = lum(i);
            ink++;
            inkLum += l;
            if (x < 2 || y < 2 || x >= N - 2 || y >= N - 2) {
              edge++;
              edgeLum += l;
            }
            if (l > 0.85) {
              const clear =
                (alpha(x - 3, y) < 32 ? 1 : 0) +
                (alpha(x + 3, y) < 32 ? 1 : 0) +
                (alpha(x, y - 3) < 32 ? 1 : 0) +
                (alpha(x, y + 3) < 32 ? 1 : 0);
              if (clear >= 2) floatingWhite++;
            }
          }
        }
        const edgeCells = N * 4 * 2 - 16;
        if (edge / edgeCells > 0.8) verdict = edgeLum / edge < 0.5 ? 'dark' : 'light';
        else if (ink && inkLum / ink > 0.7) verdict = 'dark'; // white-ink logo
        else if (ink && floatingWhite / ink > 0.03) verdict = 'dark';
      } catch {
        /* tainted canvas — leave the logo untiled */
      }
    }
  }
  groundCache.set(url, verdict);
  return verdict;
}

export function useLogoGround(
  url: string | null | undefined,
  /** A ground already known from `groundFromFlag` — skips the probe entirely. */
  known?: Ground | null
): Ground {
  const [ground, setGround] = useState<Ground>(() => {
    if (known != null) return known;
    return url ? (groundCache.get(url) ?? 'none') : 'none';
  });
  useEffect(() => {
    if (known != null) {
      setGround(known);
      return;
    }
    if (!url) return setGround('none');
    let alive = true;
    measureGround(url).then(g => alive && setGround(g));
    return () => {
      alive = false;
    };
  }, [url, known]);
  return ground;
}
